import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useAppContext } from 'src/contexts/AppContext';
import { useEventMapContext } from 'src/contexts/EventMapContext';
import { useLocalizationContext } from 'src/contexts/LocalizationContext';
import { useGetEventAutoPricingSettings } from 'src/hooks/useGetEventAutoPricingSettings';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { EventPricingSeatMapForm } from 'src/utils/eventWithDataUtils';
import { getCompleteEventConfigScoreOverrides } from 'src/utils/seatScoreUtils';
import {
  Feature,
  ScoreModel,
  SectionScoreOverride,
} from 'src/WebApiController';

import { EventAutoPricingSettingsFormContent } from './components/EventAutoPricingSettingsFormContent/EventAutoPricingSettingsFormContent';

export type EventScoreUpdate = {
  newScoreOverrides?: SectionScoreOverride[];
  newConfigPayload?: string;
};

export const EventPricingSeatMapBody = ({
  hasEventPricingTeamFeature,
}: {
  hasEventPricingTeamFeature?: boolean;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { watch, setValue } = useFormContext<EventPricingSeatMapForm>();

  const { event, venueMapInfo, venueMapsByScoreModelQuery } =
    useEventMapContext();
  const { loginContext } = useAppContext();
  const { getUiCurrency } = useLocalizationContext();

  const uiCurrency = useMemo(
    () => getUiCurrency(loginContext?.user?.activeAccount?.currencyCode),
    [getUiCurrency, loginContext?.user?.activeAccount?.currencyCode]
  );

  const { pricingSettings } = useGetEventAutoPricingSettings(event!);
  const hasAllowSeatScoreCompListingModeFeature = useUserHasFeature(
    Feature.AllowSeatScoreCompListingMode
  );

  const sanitizedScoreModel = useMemo(() => {
    const scoreModel = pricingSettings?.scoreModel;
    if (scoreModel) return scoreModel;

    const { AdvancedVenueGeometry, MergedVenueGeometry } =
      venueMapsByScoreModelQuery.data ?? {};
    if (AdvancedVenueGeometry) return ScoreModel.AdvancedVenueGeometry;
    if (MergedVenueGeometry) return ScoreModel.MergedVenueGeometry;
    return ScoreModel.VenueGeometry;
  }, [pricingSettings?.scoreModel, venueMapsByScoreModelQuery.data]);

  const modeledSectionScores = useMemo(() => {
    switch (sanitizedScoreModel) {
      case ScoreModel.AdvancedVenueGeometry:
        return venueMapsByScoreModelQuery.data?.AdvancedVenueGeometry;

      case ScoreModel.MergedVenueGeometry:
        return venueMapsByScoreModelQuery.data?.MergedVenueGeometry;

      default:
        return venueMapsByScoreModelQuery.data?.VenueGeometry;
    }
  }, [
    sanitizedScoreModel,
    venueMapsByScoreModelQuery.data?.AdvancedVenueGeometry,
    venueMapsByScoreModelQuery.data?.MergedVenueGeometry,
    venueMapsByScoreModelQuery.data?.VenueGeometry,
  ]);

  // Hook form fields
  const scoreOverrides =
    watch('eventScoreOverride.scoreOverrides') ??
    getCompleteEventConfigScoreOverrides(modeledSectionScores?.sectionScores);
  const scoreModel = watch('eventAutoPricing.scoreModel');

  useEffect(() => {
    if (sanitizedScoreModel !== scoreModel) {
      setValue('eventAutoPricing.scoreModel', sanitizedScoreModel);
    }
  }, [sanitizedScoreModel, scoreModel, setValue]);

  const hasAnyScore = useMemo(
    () => scoreOverrides?.some((s) => s.score),
    [scoreOverrides]
  );

  if (!event) {
    return null;
  }

  return (
    <>
      <EventAutoPricingSettingsFormContent
        venueMapSections={venueMapInfo?.sections}
        hasSeatScores={hasAnyScore}
        uiCurrency={uiCurrency}
        disabled={isLoading}
        allowSeatScoreCompListingMode={hasAllowSeatScoreCompListingModeFeature}
        hasEventPricingTeamFeature={hasEventPricingTeamFeature}
      />
    </>
  );
};
